import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as orderService from '../../services/order';

// Async thunks
export const fetchOrders = createAsyncThunk(
  'order/fetchOrders',
  async (params, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrders(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOrder = createAsyncThunk(
  'order/fetchOrder',
  async (id, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrder(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateOrder = createAsyncThunk(
  'order/updateOrder',
  async ({ id, orderData }, { rejectWithValue }) => {
    try {
      const response = await orderService.updateOrder(id, orderData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteOrder = createAsyncThunk(
  'order/deleteOrder',
  async (id, { rejectWithValue }) => {
    try {
      await orderService.deleteOrder(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOrderStats = createAsyncThunk(
  'order/fetchOrderStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrderStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOrderStatusCounts = createAsyncThunk(
  'order/fetchOrderStatusCounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await orderService.getOrderStatusCounts();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  orders: [],
  currentOrder: null,
  stats: null,
  statusCounts: {},
  loading: false,
  error: null,
  totalOrders: 0,
  lastFetchParams: {},
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    setCurrentOrder: (state, action) => {
      state.currentOrder = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.lastFetchParams = action.meta?.arg || {};
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        // Handle new response format with 'data' field
        state.orders = action.payload.data || action.payload.results || action.payload;
        state.totalOrders = action.payload.count || (action.payload.data ? action.payload.data.length : action.payload.results ? action.payload.results.length : action.payload.length || 0);
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single order
      .addCase(fetchOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update order
      .addCase(updateOrder.fulfilled, (state, action) => {
        const index = state.orders.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
        if (state.currentOrder?.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      // Delete order
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.orders = state.orders.filter(o => o.id !== action.payload);
        if (state.currentOrder?.id === action.payload) {
          state.currentOrder = null;
        }
      })
      // Fetch order stats
      .addCase(fetchOrderStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      // Fetch order status counts
      .addCase(fetchOrderStatusCounts.fulfilled, (state, action) => {
        state.statusCounts = action.payload;
      });
  },
});

export const { clearError, clearCurrentOrder, setCurrentOrder } = orderSlice.actions;
export default orderSlice.reducer; 
